import React from 'react'
import type { LucideIcon } from 'lucide-react'

export type ConfirmActionVariant = 'success' | 'warning' | 'danger' | 'neutral'

export interface ConfirmActionModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void | Promise<void>
  title: string
  message: React.ReactNode
  confirmLabel: string
  cancelLabel?: string
  isLoading?: boolean
  /** Ícone exibido no círculo à esquerda */
  icon: LucideIcon
  variant?: ConfirmActionVariant
}

const variantStyles: Record<
  ConfirmActionVariant,
  { ring: string; iconBg: string; iconColor: string; confirmBtn: string; confirmBtnHover: string; focusRing: string }
> = {
  success: {
    ring: 'ring-emerald-500/15',
    iconBg: 'bg-emerald-50',
    iconColor: 'text-emerald-600',
    confirmBtn: 'bg-emerald-600',
    confirmBtnHover: 'hover:bg-emerald-700',
    focusRing: 'focus:ring-emerald-500'
  },
  warning: {
    ring: 'ring-amber-500/15',
    iconBg: 'bg-amber-50',
    iconColor: 'text-amber-600',
    confirmBtn: 'bg-amber-600',
    confirmBtnHover: 'hover:bg-amber-700',
    focusRing: 'focus:ring-amber-500'
  },
  danger: {
    ring: 'ring-red-500/15',
    iconBg: 'bg-red-50',
    iconColor: 'text-red-600',
    confirmBtn: 'bg-red-600',
    confirmBtnHover: 'hover:bg-red-700',
    focusRing: 'focus:ring-red-500'
  },
  neutral: {
    ring: 'ring-slate-500/15',
    iconBg: 'bg-slate-100',
    iconColor: 'text-slate-700',
    confirmBtn: 'bg-slate-800',
    confirmBtnHover: 'hover:bg-slate-900',
    focusRing: 'focus:ring-slate-500'
  }
}

/**
 * Modal reutilizável para confirmar ações (finalizar, arquivar, etc.).
 * Layout moderno com ícone, texto e botões Cancelar / Confirmar.
 */
export const ConfirmActionModal: React.FC<ConfirmActionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel = 'Cancelar',
  isLoading = false,
  icon: Icon,
  variant = 'neutral'
}) => {
  const styles = variantStyles[variant]

  const handleConfirm = async () => {
    await onConfirm()
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/45 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-action-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isLoading) onClose()
      }}
    >
      <div
        className={`bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden ring-1 ${styles.ring}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 sm:p-7">
          <div className="flex items-start gap-4">
            <div
              className={`flex-shrink-0 w-12 h-12 rounded-2xl ${styles.iconBg} flex items-center justify-center shadow-inner`}
            >
              <Icon className={`w-6 h-6 ${styles.iconColor}`} aria-hidden />
            </div>
            <div className="flex-1 min-w-0 pt-0.5">
              <h3 id="confirm-action-title" className="text-lg font-semibold tracking-tight text-gray-900">
                {title}
              </h3>
              <div className="mt-2 text-sm text-gray-600 leading-relaxed">{message}</div>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 mt-8">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="w-full sm:w-auto px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:opacity-50 transition-colors"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isLoading}
              className={`w-full sm:w-auto px-4 py-2.5 text-sm font-medium text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-offset-2 ${styles.confirmBtn} ${styles.confirmBtnHover} ${styles.focusRing} disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2 min-h-[42px]`}
            >
              {isLoading ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Aguarde...
                </>
              ) : (
                confirmLabel
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
